import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { UserContext } from '../../UserContext';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import { usePreventDoubleClick } from '../hooks/usePreventDoubleClick';

import styles from './AuditoriaDistribuidoraDashboard.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

function AuditoriaDistribuidoraDashboard() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const { get } = useHttpClient();
    const { exibirErro } = useNotificacoes();

    const hoje = new Date();
    const competenciaAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;

    const [usinas, setUsinas] = useState([]);
    const [filtros, setFiltros] = useState({
        idUsina: '',
        competencia: competenciaAtual
    });
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mostrarDetalhes, setMostrarDetalhes] = useState(false);
    const buscou = useRef(false);

    useEffect(() => {
        if (user === null) {
            navigate('/login');
        }
    }, [user, navigate]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const idUsinaUrl = params.get('idUsina');
        const competenciaUrl = params.get('competencia');

        if (idUsinaUrl || competenciaUrl) {
            setFiltros((prev) => ({
                ...prev,
                idUsina: idUsinaUrl || prev.idUsina,
                competencia: competenciaUrl || prev.competencia
            }));
        }
    }, [location.search]);

    useEffect(() => {
        if (!user?.empresa?.id) return;

        let ativo = true;

        const carregarUsinas = async () => {
            try {
                const resp = await get(`/hello-sol/usinas/buscar?idEmpresa=${user.empresa.id}`);
                if (!ativo) return;
                setUsinas(Array.isArray(resp) ? resp : (resp.content ?? []));
            } catch (err) {
                if (ativo) exibirErro(err.message || 'Erro ao carregar usinas.');
            }
        };

        carregarUsinas();

        return () => {
            ativo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.empresa?.id]);

    const buscarDashboard = async () => {
        if (!filtros.idUsina) {
            exibirErro('Selecione a usina.');
            return;
        }

        if (!filtros.competencia) {
            exibirErro('Selecione a competência.');
            return;
        }

        setLoading(true);
        try {
            const resp = await get(
                `/hello-sol/auditoria-distribuidora/dashboard?idUsina=${filtros.idUsina}&competencia=${filtros.competencia}`
            );
            setDados(resp);
            buscou.current = true;
        } catch (err) {
            setDados(null);
            buscou.current = true;
            exibirErro(err.message || 'Erro ao carregar auditoria da distribuidora.');
        } finally {
            setLoading(false);
        }
    };

    const [safeFetch] = usePreventDoubleClick(buscarDashboard);

    const handleSubmit = (e) => {
        e.preventDefault();
        safeFetch();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFiltros((prev) => ({ ...prev, [name]: value }));
    };

    const irParaAnaliseRateio = () => {
        if (!filtros.idUsina) {
            exibirErro('Selecione a usina para abrir a análise de rateio.');
            return;
        }

        navigate(`/home/rateio-analise?idUsina=${filtros.idUsina}&mes=${filtros.competencia}`);
    };

    const formatNumber = (valor, scale = 2) =>
        Number(valor || 0).toLocaleString('pt-BR', {
            minimumFractionDigits: scale,
            maximumFractionDigits: scale
        });

    const getStatusClass = (status) => {
        if (status === 'OK') return styles.statusOk;
        if (status === 'ALERTA') return styles.statusAlerta;
        if (status === 'CRITICO') return styles.statusCritico;
        return styles.statusDefault;
    };

    const getDiffClass = (valor) => {
        const numero = Number(valor || 0);
        if (numero > 0) return styles.valorPositivo;
        if (numero < 0) return styles.valorNegativo;
        return styles.valorNeutro;
    };

    const getResultadoAuditoriaClass = (status) => {
        if (status === 'OK') return styles.resultadoOk;
        if (status === 'ALERTA') return styles.resultadoAlerta;
        if (status === 'CRITICO') return styles.resultadoCritico;
        return styles.resultadoDefault;
    };

    const itens = dados?.itens ?? [];
    const insights = dados?.insights ?? [];

    const divergencia = Number(dados?.divergenciaNaoExplicada || 0);
    const energiaInjetada = Number(dados?.energiaInjetadaUsina || 0);
    const impactoPercentual = energiaInjetada > 0
        ? Math.abs(divergencia) * 100 / energiaInjetada
        : 0;

    const resumoResultadoAuditoria = (() => {
        if (!dados) return '';

        if ((dados.statusGeral || '').toUpperCase() === 'OK') {
            return `Não foi identificada divergência relevante na competência analisada. A diferença apurada foi de ${formatNumber(dados.divergenciaNaoExplicada)} kWh, equivalente a ${formatNumber(impactoPercentual)}% da energia injetada.`;
        }

        if ((dados.statusGeral || '').toUpperCase() === 'ALERTA') {
            return `Foi identificada divergência moderada de ${formatNumber(dados.divergenciaNaoExplicada)} kWh, equivalente a ${formatNumber(impactoPercentual)}% da energia injetada. O cenário sugere atenção na conferência da compensação e dos saldos da distribuidora.`;
        }

        return `Foi identificada divergência crítica de ${formatNumber(dados.divergenciaNaoExplicada)} kWh, equivalente a ${formatNumber(impactoPercentual)}% da energia injetada. Há indício de perda ou inconsistência entre a energia injetada, a compensação realizada e os saldos apurados pela distribuidora.`;
    })();

    return (
        <div className={styles_global.container}>
            <h2>Auditoria da Distribuidora</h2>

            <form className={styles.filterForm} onSubmit={handleSubmit}>
                <div className={`${styles_content.formField} ${styles.usinaField}`}>
                    <label>Usina</label>
                    <select
                        name="idUsina"
                        value={filtros.idUsina}
                        onChange={handleChange}
                    >
                        <option value="">Selecione a Usina</option>
                        {usinas.map((u) => (
                            <option key={u.id} value={u.id}>
                                {u.nome}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={`${styles_content.formField} ${styles.mesField}`}>
                    <label>Competência</label>
                    <input
                        type="month"
                        name="competencia"
                        value={filtros.competencia}
                        onChange={handleChange}
                    />
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>

                    <button
                        type="button"
                        onClick={irParaAnaliseRateio}
                        disabled={!filtros.idUsina}
                    >
                        Ver Análise de Rateio
                    </button>
                </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}

            {!loading && dados && (
                <>
                    <div className={styles.resumoExecutivoBox}>
                        <h3>Resumo Executivo</h3>
                        <p>{dados.resumoExecutivo}</p>
                    </div>

                    <div className={styles.dashboardGrid}>
                        <div className={styles.card}>
                            <h4>Energia Injetada</h4>
                            <p>{formatNumber(dados.energiaInjetadaUsina)} kWh</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Saldo Anterior</h4>
                            <p>{formatNumber(dados.saldoAnteriorTotal)} kWh</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Energia Compensada</h4>
                            <p>{formatNumber(dados.energiaCompensadaTotal)} kWh</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Saldo Atual</h4>
                            <p>{formatNumber(dados.saldoAtualTotal)} kWh</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Divergência</h4>
                            <p>{formatNumber(dados.divergenciaNaoExplicada)} kWh</p>
                        </div>

                        <div className={`${styles.card} ${styles.cardDestaque}`}>
                            <h4>Status Geral</h4>
                            <p>
                                <span className={`${styles.statusBadge} ${getStatusClass(dados.statusGeral)}`}>
                                    {dados.statusGeral || '—'}
                                </span>
                            </p>
                        </div>
                    </div>

                    <div className={`${styles.resultadoAuditoriaBox} ${getResultadoAuditoriaClass(dados.statusGeral)}`}>
                        <h3>Resultado da Auditoria</h3>
                        <p>{resumoResultadoAuditoria}</p>
                        <div className={styles.resultadoAuditoriaMeta}>
                            <span><strong>Divergência apurada:</strong> {formatNumber(dados.divergenciaNaoExplicada)} kWh</span>
                            <span><strong>Impacto:</strong> {formatNumber(impactoPercentual)}%</span>
                            <span>
                                <strong>Classificação:</strong>{' '}
                                <span className={`${styles.statusBadge} ${getStatusClass(dados.statusGeral)}`}>
                                    {dados.statusGeral || '—'}
                                </span>
                            </span>
                        </div>
                    </div>

                    <div className={styles.acoesTabela}>
                        <button
                            type="button"
                            className={styles.botaoSecundario}
                            onClick={() => setMostrarDetalhes((prev) => !prev)}
                        >
                            {mostrarDetalhes ? 'Ocultar detalhes' : 'Ver detalhes'}
                        </button>
                    </div>

                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                            <tr>
                                <th>UNIDADE</th>
                                <th>% RATEIO</th>
                                <th>COMPENSADA</th>
                                <th>DIF. KWH</th>
                                <th>DESVIO</th>
                                <th>STATUS</th>
                                {mostrarDetalhes && (
                                    <>
                                        <th>ESP. RATEIO</th>
                                        <th>CRÉDITO</th>
                                        <th>SALDO</th>
                                    </>
                                )}
                            </tr>
                            </thead>
                            <tbody>
                            {itens.map((item) => (
                                <tr key={`${item.idRateio}-${item.unidadeId}`}>
                                    <td>{item.nomeUnidade}</td>
                                    <td>{formatNumber(item.percentualRateio, 4)}%</td>
                                    <td>{formatNumber(item.energiaCompensadaUc)} kWh</td>
                                    <td className={getDiffClass(item.diferencaKwh)}>
                                        {formatNumber(item.diferencaKwh)} kWh
                                    </td>
                                    <td>{formatNumber(item.desvioPercentual)}%</td>
                                    <td>
                                            <span className={`${styles.statusBadge} ${getStatusClass(item.status)}`}>
                                                {item.status}
                                            </span>
                                    </td>

                                    {mostrarDetalhes && (
                                        <>
                                            <td>{formatNumber(item.energiaEsperadaRateio)} kWh</td>
                                            <td>{formatNumber(item.creditoRecebidoUc)} kWh</td>
                                            <td>{formatNumber(item.saldoAtualUc)} kWh</td>
                                        </>
                                    )}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {insights.length > 0 && (
                        <div className={styles.insightsBox}>
                            <h3>Insights</h3>
                            <ul>
                                {insights.map((i, idx) => (
                                    <li key={idx}>{i}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}

            {!loading && buscou.current && !dados && (
                <p className={styles_global.noData}>Nenhum dado encontrado.</p>
            )}
        </div>
    );
}

export default AuditoriaDistribuidoraDashboard;